/**
 * Connects the SyncEngine to the server and the mirror:
 * - start: mirror → graph (instant), else snapshot; restore the outbox on top;
 * - local transactions: applied at once, persisted in the outbox, pushed in batches;
 * - WebSocket: hello with the cursor, then server ops are rebased under pending ones;
 * - graph changes are written back to IndexedDB in batches.
 * Never a dialog: status goes to a quiet dot (SPEC §12).
 */
import {
  cryptoRand,
  Graph,
  ResyncNeeded,
  SyncEngine,
  uuidv7,
  type NodeId,
  type OpBody,
  type ServerMessage,
} from '@solar/core'
import { api, Unauthorized } from './api.ts'
import { Mirror } from './mirror.ts'

export interface SyncStatus {
  readonly state: 'starting' | 'online' | 'offline'
  readonly pending: number
  readonly error: string | null
}

export interface SyncHooks {
  onReinverse?: (txnId: string, inverse: OpBody[]) => void
  onDrop?: (txnId: string, reason: string) => void
  onUnauthorized?: () => void
}

export class SyncClient {
  readonly g = new Graph()
  engine!: SyncEngine
  private readonly mirror: Mirror
  private readonly hooks: SyncHooks
  private ws: WebSocket | null = null
  private wsBackoff = 1000
  private pushTimer: ReturnType<typeof setTimeout> | null = null
  private pushing = false
  private pushBackoff = 1000
  private dirty = new Set<NodeId>()
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private stopped = false
  private statusValue: SyncStatus = { state: 'starting', pending: 0, error: null }
  private readonly statusListeners = new Set<() => void>()
  /** Run when the page goes away, before the outbox is written and sent (the store commits typing). */
  readonly onExit = new Set<() => void>()

  constructor(userId: string, hooks: SyncHooks = {}) {
    this.mirror = new Mirror(userId)
    this.hooks = hooks
  }

  get status(): SyncStatus {
    return this.statusValue
  }

  subscribeStatus = (fn: () => void): (() => void) => {
    this.statusListeners.add(fn)
    return () => this.statusListeners.delete(fn)
  }

  private setStatus(patch: Partial<SyncStatus>): void {
    this.statusValue = { ...this.statusValue, ...patch, pending: this.engine?.pendingCount ?? 0 }
    this.statusListeners.forEach((f) => f())
  }

  async start(): Promise<void> {
    const state = await this.mirror.load()
    const clientId = state.clientId ?? `web-${uuidv7().slice(-12)}`
    if (!state.clientId) await this.mirror.setClientId(clientId)
    this.engine = new SyncEngine(this.g, clientId, {
      newId: () => uuidv7(),
      now: () => Date.now(),
      rand: cryptoRand,
      clientSeq: state.clientSeq,
      onReinverse: this.hooks.onReinverse,
      onDrop: (id, reason) => {
        console.warn('solar: change dropped', id, reason)
        this.hooks.onDrop?.(id, reason)
      },
    })
    if (state.cursor >= 0) {
      this.engine.loadSnapshot(state.nodes, state.cursor)
    } else {
      const snap = await this.call(() => api.snapshot())
      if (!snap) return
      this.engine.loadSnapshot(snap.nodes, snap.seq)
      await this.mirror.replaceAll(snap.nodes, snap.seq)
    }
    if (state.outbox.length) this.engine.restorePending(state.outbox)
    this.g.subscribe((cs) => {
      for (const id of cs.nodes) this.dirty.add(id)
      this.scheduleFlush()
    })
    this.connect()
    if (this.engine.pendingCount) this.schedulePush(0)
    addEventListener('online', () => {
      this.schedulePush(0)
      this.connect()
    })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.flush()
      else this.connect()
    })
    // Leaving the page (reload, tab closed): the outbox goes out with a keepalive request, which
    // the browser finishes after the page is gone. Resending later is harmless (idempotent).
    addEventListener('pagehide', () => {
      this.onExit.forEach((f) => f())
      void this.flush()
      this.pushOnExit()
    })
    this.setStatus({})
  }

  /**
   * Apply ops as one local transaction (one undo unit) and queue them for the server. The outbox
   * is written to IndexedDB at once (with any base records waiting to be written, so the mirror
   * stays consistent): an edit survives a reload or a closed tab a moment later.
   */
  local(ops: readonly OpBody[], ts: number): { txnId: string; inverse: OpBody[] } {
    const r = this.engine.local(ops, ts)
    void this.flush()
    this.setStatus({})
    this.schedulePush(150)
    return r
  }

  private pushOnExit(): void {
    if (this.engine.pendingCount === 0) return
    const body = JSON.stringify({ clientId: this.engine.clientId, txns: this.engine.outbox() })
    // keepalive bodies are capped at 64 KB; a larger outbox waits in IndexedDB for the next start.
    if (body.length > 60_000) return
    void fetch('/api/ops', { method: 'POST', body, keepalive: true, credentials: 'same-origin', headers: { 'content-type': 'application/json' } }).catch(() => {})
  }

  /** Mark nodes whose base version may change when pending transactions settle. */
  private settle<T>(fn: () => T): T {
    for (const id of this.engine.touchedByPending()) this.dirty.add(id)
    const r = fn()
    for (const id of this.engine.touchedByPending()) this.dirty.add(id)
    this.scheduleFlush()
    return r
  }

  stop(): void {
    this.stopped = true
    this.ws?.close()
    if (this.pushTimer) clearTimeout(this.pushTimer)
  }

  async logoutAndForget(): Promise<void> {
    this.stop()
    await this.mirror.destroy()
  }

  // ------------------------------------------------------------------ push

  private schedulePush(delay: number): void {
    if (this.stopped || this.pushTimer) return
    this.pushTimer = setTimeout(() => {
      this.pushTimer = null
      void this.pushNow()
    }, delay)
  }

  private async pushNow(): Promise<void> {
    if (this.pushing || this.engine.pendingCount === 0) return
    this.pushing = true
    try {
      const res = await api.push({ clientId: this.engine.clientId, txns: this.engine.outbox() })
      this.settle(() => this.engine.acknowledge(res))
      this.pushBackoff = 1000
      this.setStatus({ error: null })
      if (this.engine.pendingCount) this.schedulePush(0)
    } catch (e) {
      if (e instanceof Unauthorized) return this.hooks.onUnauthorized?.()
      this.setStatus({ state: 'offline', error: e instanceof Error ? e.message : String(e) })
      this.pushTimer = setTimeout(() => {
        this.pushTimer = null
        void this.pushNow()
      }, this.pushBackoff)
      this.pushBackoff = Math.min(this.pushBackoff * 2, 30_000)
    } finally {
      this.pushing = false
    }
  }

  // ------------------------------------------------------------------ WebSocket

  private connect(): void {
    if (this.stopped || (this.ws && this.ws.readyState <= WebSocket.OPEN)) return
    const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/sync`)
    this.ws = ws
    ws.onopen = () => {
      this.wsBackoff = 1000
      ws.send(JSON.stringify({ type: 'hello', since: this.engine.cursor }))
      this.setStatus({ state: 'online', error: null })
    }
    ws.onmessage = (ev) => {
      const msg = JSON.parse(String(ev.data)) as ServerMessage
      if (msg.type === 'resync') return void this.resync()
      try {
        this.settle(() => this.engine.receive(msg.ops))
        this.setStatus({})
      } catch (e) {
        if (e instanceof ResyncNeeded) void this.resync()
        else throw e
      }
    }
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null
      if (this.stopped) return
      this.setStatus({ state: 'offline' })
      void api.me().catch((e) => {
        if (e instanceof Unauthorized) this.hooks.onUnauthorized?.()
      })
      setTimeout(() => this.connect(), this.wsBackoff)
      this.wsBackoff = Math.min(this.wsBackoff * 2, 30_000)
    }
  }

  private async resync(): Promise<void> {
    const snap = await this.call(() => api.snapshot())
    if (!snap) return
    this.engine.loadSnapshot(snap.nodes, snap.seq)
    await this.mirror.replaceAll(this.engine.baseNodes(), snap.seq)
    this.dirty.clear()
    this.scheduleFlush()
  }

  // ------------------------------------------------------------------ persistence

  private scheduleFlush(): void {
    if (this.flushTimer) return
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      void this.flush()
    }, 300)
  }

  private async flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
    const ids = [...this.dirty]
    this.dirty.clear()
    const put = []
    const del = []
    for (const id of ids) {
      const base = this.engine.baseOf(id)
      if (base) put.push(base)
      else del.push(id)
    }
    await this.mirror.persist({
      put,
      del,
      cursor: this.engine.cursor,
      outbox: this.engine.outbox(),
      clientSeq: this.engine.clientSeq,
    })
  }

  private async call<T>(fn: () => Promise<T>): Promise<T | null> {
    try {
      return await fn()
    } catch (e) {
      if (e instanceof Unauthorized) this.hooks.onUnauthorized?.()
      else this.setStatus({ state: 'offline', error: e instanceof Error ? e.message : String(e) })
      return null
    }
  }
}
